import React, { useCallback } from 'react';

import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { DropdownMenuTypePropType } from 'ui-components/components/dropdown-menu/dropdown-menu-type';
import { DropdownMenuPositionPropType } from 'ui-components/prop-types/dropdown-menu-position';

import { ManagePresetsModal } from '../../components/modals/manage-presets-modal';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { ComponentStates } from '../../enums/component-states';
import { InsuranceValues } from '../../enums/insurance-values';
import { useModal } from '../../hooks/use-modal';
import { FormattedMessagePropType } from '../../prop-types/intl';
import { presetService } from '../../services/preset-service';
import * as Actions from '../../store/csw/csw.actions';
import { FulfillmentPlannerProperties } from '../../store/csw/fulfillment-planner-properties';
import Global from '../../store/global-selectors';
import { useFulfillmentPlannerContext } from '../context';
import { isShipFromExternal } from '../../store/csw/csw.fulfillment-plan-to-csw';

import Presets from './index';

const PresetsWrapper = ({
  id,
  dropdownMenuType,
  fulfillmentPlanId,
  dropdownTogglerMessage,
  dropdownTogglerClassName,
  chevronClassName,
  menuPosition,
}) => {
  const dispatch = useDispatch();
  const { close: closeManagePresetsModal, isOpen: isManagePresetsModalOpen, open: openManagePresetsModal } = useModal();
  const { presets } = useSelector((state) => new FulfillmentPlannerProperties({ state, fulfillmentPlanId }));
  const { applyPresetConfiguration, uiRestrictions, shipFromId } = useFulfillmentPlannerContext();

  const hidePresets = uiRestrictions?.applyPresets?.componentState === ComponentStates.Hidden;

  const sellerShipFroms = useSelector(Global.selectSellerShipFroms) || emptyArray;
  const onPresetSelected = useCallback(
    (preset) => {
      const isExternal = isShipFromExternal(shipFromId);
      const payload = presetService.generateShipmentConfigPayload(preset, sellerShipFroms);
      if (isExternal && !payload?.presetOptions?.shipFromId) {
        return;
      }
      if (applyPresetConfiguration) {
        applyPresetConfiguration(payload);
      }

      const { packages, options, presetOptions } = payload || emptyObject;

      if (packages?.length) {
        dispatch(Actions.updateShipmentConfigurationPackagesFromPreset(fulfillmentPlanId, ...packages));
      }

      const confirmation = options?.confirmation;
      if (confirmation) {
        dispatch(Actions.updateShipmentConfigurationOptions(fulfillmentPlanId, 'confirmation', confirmation.confirmationId));
      }

      if (presetOptions && Object.keys(presetOptions).length) {
        dispatch(Actions.updateShipmentConfiguration(fulfillmentPlanId, presetOptions));
        if (presetOptions?.insuranceProvider) {
          const insuranceValue = InsuranceValues.Sum.name;
          dispatch(Actions.changeInsuredValue(fulfillmentPlanId, insuranceValue));
        }
      }
    },
    [dispatch, fulfillmentPlanId, sellerShipFroms, applyPresetConfiguration, shipFromId],
  );

  return (
    !hidePresets && (
      <>
        <Presets
          chevronClassName={chevronClassName}
          dropdownMenuType={dropdownMenuType}
          dropdownTogglerClassName={dropdownTogglerClassName}
          dropdownTogglerMessage={dropdownTogglerMessage}
          id={id}
          menuPosition={menuPosition}
          onManagePresetsClick={openManagePresetsModal}
          onPresetSelected={onPresetSelected}
          presets={presets}
        />
        {isManagePresetsModalOpen && <ManagePresetsModal onClose={closeManagePresetsModal} />}
      </>
    )
  );
};

PresetsWrapper.propTypes = {
  id: PropTypes.string,
  dropdownMenuType: DropdownMenuTypePropType,
  fulfillmentPlanId: PropTypes.string.isRequired,
  dropdownTogglerMessage: FormattedMessagePropType,
  dropdownTogglerClassName: PropTypes.string,
  chevronClassName: PropTypes.string,
  menuPosition: DropdownMenuPositionPropType,
};

export default PresetsWrapper;
